import * as React from "react";
import { styled, css } from "styled-components";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { cssSpacing, surfaceColorMap } from "../../util/styles.js";
import type { BackgroundColor } from "../../types/theme.js";
import { info } from "../../icons/index.js";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { RichText } from "../../internal-components/RichText.js";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";

export interface Props {
  title?: string;
  text?: string;
  info?: string;
  expandText?: string;
  collapseText?: string;
  backgroundColor?: BackgroundColor;
}

export const ShortVersion: React.FunctionComponent<Props> = ({
  title,
  text,
  info: infoText,
  expandText,
  collapseText,
  backgroundColor = "surface2",
}) => {
  const [show, setShow] = React.useState(false);
  const [showInfo, setShowInfo] = React.useState(false);

  const toggle = React.useCallback(() => setShow((s) => !s), []);
  const toggleInfo = React.useCallback(() => setShowInfo((s) => !s), []);

  if (!text) {
    return null;
  }

  return (
    <VerticalSpacer>
      <HorizontalSpacer>
        <Constrainer $width="small">
          <Content $show={show} $bg={backgroundColor}>
            <Title>
              <h4 {...createEditableProps("text", "title")}>
                {title ? title : "Korversjonen"}
              </h4>
              {infoText && <InfoButton onClick={toggleInfo}>{info}</InfoButton>}
            </Title>
            {showInfo && infoText && (
              <RichText
                className="short-version-info"
                dangerouslySetInnerHTML={{ __html: infoText }}
                {...createEditableProps("html", "info")}
              />
            )}
            <RichText
              dangerouslySetInnerHTML={{ __html: text }}
              {...createEditableProps("html", "text")}
            />
            <ButtonWrapper $show={show} $bg={backgroundColor}>
              <button onClick={toggle} className="h5">
                {show
                  ? collapseText || "Vis mindre"
                  : expandText || "Vis mer"}
              </button>
            </ButtonWrapper>
          </Content>
        </Constrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

export const Content = styled.div<{ $show: boolean; $bg: BackgroundColor }>`
  background-color: ${({ $bg }) => surfaceColorMap[$bg]};
  color: ${({ $bg }) => ($bg === "surface3" ? "#fff" : "var(--swatch2)")};
  svg path { fill: currentColor; stroke: currentColor; }
  position: relative;
  padding: ${cssSpacing("l")};
  border-radius: ${(props) => props.theme.borderRadius};
  max-height: 200px;
  box-sizing: border-box;
  overflow: hidden;

  .rich-text {
    margin: ${cssSpacing("xs", 0, "l", 0)};
  }

  .short-version-info {
    font-size: 0.8rem;
    font-style: italic;
    margin-bottom: ${(props) => props.theme.spacing.xs};
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${cssSpacing("l", "xl")};
  }

  ${(props) =>
    props.$show &&
    css`
      max-height: 100%;
    `};
`;

export const Title = styled.div`
  display: flex;
  justify-content: space-between;

  h4 {
    margin: 0;
  }
`;
export const InfoButton = styled.button`
  display: flex;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const ButtonWrapper = styled.div<{ $show: boolean; $bg: BackgroundColor }>`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  position: absolute;
  bottom: 0;
  left: 50%;
  height: 100px;
  width: 100%;
  transform: translateX(-50%);
  padding: ${cssSpacing(0, 0, "s", 0)};
  background: linear-gradient(transparent, ${(p) => surfaceColorMap[p.$bg]} 50%);

  ${(props) =>
    props.$show &&
    css`
      background: none;
    `}

  button {
    background: none;
    border: none;
    border-bottom: 1px solid currentColor;
    padding: 0;
    cursor: pointer;
    color: inherit;
  }
`;
